import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MdArrowBack, MdRefresh, MdReceiptLong, MdContentCopy, MdCheck } from "react-icons/md";
import Navbar from "../nav_bar/Navbar";
import { fetchAllTransactions } from "../../services/paymentHistoryService";
import "./MyTransactionsPage.css";

// ── Label maps ────────────────────────────────────────────────
const TYPE_CONFIG = {
  SUBSCRIPTION: { label: "Subscription", className: "badge-blue" },
  EVENT:        { label: "Event",         className: "badge-purple" },
  COURSE:       { label: "Course",        className: "badge-green" },
};

const STATUS_CONFIG = {
  SUCCESS: { label: "Paid",    className: "status-success" },
  FAILED:  { label: "Failed",  className: "status-failed" },
  PENDING: { label: "Pending", className: "status-pending" },
  CREATED: { label: "Pending", className: "status-pending" },
};

const SUBTITLE_MAP = {
  GROUP:            "Group Sessions",
  ONE_ON_ONE:       "One on One",
  PARTNER_PRACTICE: "Partner Practice",
  COMBO:            "Combo Plan",
};

// ── Helpers ───────────────────────────────────────────────────
const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });

const formatAmount = (amount) =>
  `₹${Number(amount).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const resolveSubTitle = (tx) => {
  if (!tx.subTitle) return null;
  return SUBTITLE_MAP[tx.subTitle] ?? tx.subTitle;
};

// ── Copy button ───────────────────────────────────────────────
const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <button className="txn-copy-btn" onClick={handleCopy} title="Copy payment ID">
      {copied ? <MdCheck className="copy-icon copied" /> : <MdContentCopy className="copy-icon" />}
    </button>
  );
};

// ── Skeleton card ─────────────────────────────────────────────
const SkeletonCard = () => (
  <div className="txn-card skeleton">
    <div className="skeleton-line w-30" />
    <div className="skeleton-line w-60" />
    <div className="skeleton-line w-40" />
  </div>
);

// ── Main component ────────────────────────────────────────────
const MyTransactionsPage = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(null);
  const navigate = useNavigate();

  const load = () => {
    setLoading(true);
    setError(null);
    fetchAllTransactions()
      .then((res) => setTransactions(res.data || []))
      .catch((err) => {
        if (err.response?.status === 401) navigate("/login");
        else setError("Failed to load transactions. Please try again.");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const totalSpent = transactions
    .filter((t) => t.status === "SUCCESS")
    .reduce((sum, t) => sum + (t.amount || 0), 0);

  return (
    <>
      <Navbar />
      <div className="txn-page">
        <div className="txn-container">

          {/* Header */}
          <div className="txn-header">
            <button className="txn-back-btn" onClick={() => navigate(-1)}>
              <MdArrowBack /> Back
            </button>
            <h1 className="txn-title">My Transactions</h1>
          </div>

          {/* Summary strip */}
          {!loading && !error && transactions.length > 0 && (
            <div className="txn-summary">
              <span className="txn-summary-label">Total Spent</span>
              <span className="txn-summary-amount">{formatAmount(totalSpent)}</span>
              <span className="txn-summary-note">across {transactions.filter(t => t.status === "SUCCESS").length} successful transaction{transactions.filter(t => t.status === "SUCCESS").length !== 1 ? "s" : ""}</span>
            </div>
          )}

          {/* Loading */}
          {loading && (
            <div className="txn-list">
              {[1, 2, 3].map((i) => <SkeletonCard key={i} />)}
            </div>
          )}

          {/* Error */}
          {!loading && error && (
            <div className="txn-empty">
              <MdReceiptLong className="txn-empty-icon error-icon" />
              <p className="txn-empty-text">{error}</p>
              <button className="txn-retry-btn" onClick={load}>
                <MdRefresh /> Retry
              </button>
            </div>
          )}

          {/* Empty */}
          {!loading && !error && transactions.length === 0 && (
            <div className="txn-empty">
              <MdReceiptLong className="txn-empty-icon" />
              <p className="txn-empty-text">No transactions yet</p>
              <p className="txn-empty-sub">Your payment history will appear here once you make a purchase.</p>
            </div>
          )}

          {/* Transaction list */}
          {!loading && !error && transactions.length > 0 && (
            <div className="txn-list">
              {transactions.map((tx) => {
                const typeConf   = TYPE_CONFIG[tx.type]   || { label: tx.type,   className: "badge-blue" };
                const statusConf = STATUS_CONFIG[tx.status] || STATUS_CONFIG.PENDING;
                const subTitle   = resolveSubTitle(tx);

                return (
                  <div key={tx.id} className="txn-card">
                    <div className="txn-card-left">
                      <div className="txn-card-top">
                        <span className={`txn-type-badge ${typeConf.className}`}>{typeConf.label}</span>
                        <span className="txn-date">{formatDate(tx.date)}</span>
                      </div>
                      <p className="txn-card-title">{tx.title}</p>
                      {subTitle && <p className="txn-card-subtitle">{subTitle}</p>}
                      {tx.razorpayPaymentId && (
                        <div className="txn-ref-row">
                          <p className="txn-ref-id">{tx.razorpayPaymentId}</p>
                          <CopyButton text={tx.razorpayPaymentId} />
                        </div>
                      )}
                    </div>
                    <div className="txn-card-right">
                      <p className="txn-amount">{formatAmount(tx.amount)}</p>
                      <span className={`txn-status-chip ${statusConf.className}`}>{statusConf.label}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

        </div>
      </div>
    </>
  );
};

export default MyTransactionsPage;
